import { format } from 'date-fns';
import {
  HOUSE_TYPES,
  FLOOR_OPTIONS,
  BIYAANO_FEATURES,
  DESTINATION_TYPES,
  GURYAHA_ITEMS,
  XAFIISYADA_ITEMS,
  SHIFT_PERIODS,
  SHIFT_VEHICLES,
  MUKAYF_IC_OPTIONS
} from './constants';

export const toJsDate = (value) => {
  if (!value) return null;
  try {
    if (value.toDate) return value.toDate();
    if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

/** Calendar date from `<input type="date">` (YYYY-MM-DD) in local timezone — avoids UTC off-by-one day. */
export const parseDateInputValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const str = String(value).trim();
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }
  return toJsDate(value);
};

export const toDateInputValue = (value) => {
  const parsed = toJsDate(value);
  if (!parsed) return '';
  try {
    return format(parsed, 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

export const getRecordDate = (item) => {
  const onDate = toJsDate(item?.date);
  if (onDate) return onDate;
  const created = toJsDate(item?.createdAt);
  if (created) return created;
  return toJsDate(item?.movingDate);
};

/** Dashboard / reports: when invoice was issued (not job moving date). */
export const getInvoiceRecordDate = (invoice) => {
  return (
    toJsDate(invoice?.createdAt) ||
    toJsDate(invoice?.approvedAt) ||
    toJsDate(invoice?.updatedAt) ||
    null
  );
};

export const formatDate = (date, formatStr = 'dd/MM/yyyy') => {
  const parsed = toJsDate(date);
  if (!parsed) return '-';
  try {
    return format(parsed, formatStr);
  } catch {
    return '-';
  }
};

/** Calendar day (shift date, moving date) without a UTC off-by-one. */
export const formatCalendarDate = (value) => {
  const parsed = parseDateInputValue(value);
  if (!parsed) return '';
  try {
    return format(parsed, 'dd/MM/yyyy');
  } catch {
    return '';
  }
};

export const toMoney = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

export const getShiftVehicleLabel = (value) => {
  if (!value) return '';
  const found = SHIFT_VEHICLES.find((v) => v.value === value);
  return found ? found.label : value;
};

/** Fill supervisor / workers / vehicle labels from employees & constants. */
export const enrichWorkShifts = (shifts, employees = []) => {
  if (!Array.isArray(shifts) || shifts.length === 0) return shifts;
  const byId = new Map((employees || []).map((e) => [e.id, e]));
  return shifts.map((s) => {
    const sup = s.supervisorEmployeeId ? byId.get(s.supervisorEmployeeId) : null;
    const phone = String(s.supervisorPhone || sup?.phone || '').trim();
    const workers = (s.workers || []).map((w) => {
      const emp = w.employeeId ? byId.get(w.employeeId) : null;
      return {
        ...w,
        employeeName: w.employeeName || emp?.name || '',
        department: w.department || emp?.department || ''
      };
    });
    const vehicleName =
      s.vehicleName || getShiftVehicleLabel(s.vehicleKey) || getShiftVehicleLabel(s.vehicleName) || '';
    return {
      ...s,
      supervisorName: s.supervisorName || sup?.name || '',
      supervisorDepartment: s.supervisorDepartment || sup?.department || '',
      supervisorPhone: phone,
      workers,
      vehicleName
    };
  });
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(toMoney(amount));
};

export const getInvoiceRevenue = (invoice) => {
  const total = toMoney(invoice?.totalAmount);
  if (total > 0) return total;
  return toMoney(invoice?.projectAmount) + toMoney(invoice?.xamaaliAmount);
};

export const getFinanceTotals = (invoices = [], expenseItems = []) => {
  const totalRevenue = invoices.reduce((sum, inv) => sum + getInvoiceRevenue(inv), 0);
  const totalExpenses = expenseItems.reduce((sum, exp) => sum + toMoney(exp.amount), 0);
  return {
    totalRevenue,
    totalExpenses,
    netProfit: totalRevenue - totalExpenses
  };
};

export const getServiceTypeLabel = (type) => {
  const types = {
    'gurista-guryaha': 'Gurista Guryaha',
    'gurista-xafiisyada': 'Gurista Xafiisyada',
    'gurista-ganacsiyada': 'Gurista Ganacsiyada'
  };
  return types[type] || type;
};

export const getHouseTypeLabel = (type) => {
  const found = HOUSE_TYPES.find((h) => h.value === type);
  return found ? found.label : type || '';
};

export const getFloorLabel = (level) => {
  if (level === null || level === undefined || level === '') return '';
  const value = String(level);
  const found = FLOOR_OPTIONS.find((f) => f.value === value);
  return found ? found.label : `Biyaanada ${value}aad`;
};

export const getDestinationLabel = (type) => {
  const found = DESTINATION_TYPES.find((d) => d.value === type);
  return found ? found.label : type || '';
};

export const getShiftPeriodLabel = (value) => {
  if (!value) return '';
  const found = SHIFT_PERIODS.find((p) => p.value === value);
  if (found) return found.label;
  if (value === 'day') return 'Maalin (Day)';
  if (value === 'night') return 'Habeen (Night)';
  return String(value);
};

export const getFeatureLabels = (features) => {
  if (!features || !Array.isArray(features)) return [];
  return features.map((f) => {
    const found = BIYAANO_FEATURES.find((bf) => bf.value === f);
    return found ? found.label : f;
  });
};

/** Legacy quotations saved item as Macuun. */
export const normalizeGuryahaItemName = (name) => name || '';

/** Standalone Mukayf row removed — mukayfIc is on every line item now. */
export const isLegacyMukayfRow = (item) =>
  item?.itemName === 'Mukayf/IC' || item?.itemName === 'Macuun';

/** Full catalog row for quotation/PDF (merges saved partial lists). */
export const mergeGuryahaQuotationItems = (items = []) => {
  const savedByName = new Map();
  (items || []).forEach((i) => {
    if (isLegacyMukayfRow(i)) return;
    savedByName.set(i.itemName, i);
  });
  return GURYAHA_ITEMS.map((catalog) => {
    const saved = savedByName.get(catalog.name);
    return {
      itemName: catalog.name,
      selection: saved?.selection || '',
      damaged: Boolean(saved?.damaged),
      quantity: Number(saved?.quantity) || 0,
      notes: saved?.notes || ''
    };
  });
};

export const mergeXafiisyadaQuotationItems = (items = []) => {
  const savedByName = new Map((items || []).map((i) => [i.itemName, i]));
  return XAFIISYADA_ITEMS.map((name) => {
    const saved = savedByName.get(name);
    return {
      itemName: name,
      damaged: Boolean(saved?.damaged),
      quantity: Number(saved?.quantity) || 0
    };
  });
};

export const formatMukayfIcLabel = (value) => {
  if (!value) return '-';
  const found = MUKAYF_IC_OPTIONS.find((o) => o.value === value);
  return found?.label || String(value);
};

export const formatItemSelectionLabel = (selection) => {
  if (!selection) return '-';
  for (const item of GURYAHA_ITEMS) {
    const opt = item.options?.find((o) => o.value === selection);
    if (opt) return opt.label;
  }
  return selection.replace(/-/g, ' ');
};

/** One-line summary for PDF / print: house type, floors, destination, biyaano features */
export const buildHouseMoveSummary = (record) => {
  if (!record?.houseType) return '';
  const parts = [getHouseTypeLabel(record.houseType)];
  if (record.houseType === 'dabaq' && record.floorLevel) {
    const fromName = String(record.dabaqNameFrom || '').trim();
    parts.push(
      `Ka gurayso: ${getFloorLabel(record.floorLevel)}${fromName ? ` (${fromName})` : ''}`
    );
  }
  if (record.destinationType) {
    const toName = String(record.dabaqNameTo || '').trim();
    if (record.destinationType === 'guri-caadi') {
      parts.push('U gurayso: Guri Caadi ah');
    } else if (record.destinationFloor) {
      parts.push(
        `U gurayso: ${getFloorLabel(record.destinationFloor)}${toName ? ` (${toName})` : ''}`
      );
    } else {
      parts.push(`U gurayso: ${getDestinationLabel(record.destinationType)}`);
    }
  }
  const features = getFeatureLabels(record.biyaanoFeatures);
  if (features.length > 0) {
    parts.push(`Features: ${features.join(', ')}`);
  }
  return parts.join(' · ');
};

export const getStatusBadgeClass = (status) => {
  const classes = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    invoiced: 'bg-blue-100 text-blue-800'
  };
  return classes[status] || 'bg-gray-100 text-gray-800';
};

export const getStatusLabel = (status) => {
  const labels = {
    pending: 'Sugaya',
    approved: 'La ansixiyay',
    rejected: 'La diiday',
    invoiced: 'Invoiced'
  };
  return labels[status] || status;
};

export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const buildAuditFields = (actor) => {
  if (!actor?.uid && !actor?.email) return {};
  return {
    createdByUid: actor.uid || null,
    createdByEmail: actor.email || null,
    updatedByUid: actor.uid || null,
    updatedByEmail: actor.email || null
  };
};

export const mergeUpdateAudit = (actor) => {
  if (!actor?.uid && !actor?.email) return {};
  return {
    updatedByUid: actor.uid || null,
    updatedByEmail: actor.email || null
  };
};

export const formatRecordAudit = (record) => {
  const created = formatDate(record?.createdAt, 'dd/MM/yyyy HH:mm');
  const updated = formatDate(record?.updatedAt, 'dd/MM/yyyy HH:mm');
  return {
    createdAtLabel: created,
    updatedAtLabel: updated,
    createdBy: record?.createdByEmail || record?.createdByName || '—',
    updatedBy: record?.updatedByEmail || record?.updatedByName || '—'
  };
};

/** Expenses + shift assignments for employee detail. */
export const getEmployeeWorkHistory = (employee, { expenses = [], invoices = [], quotations = [] } = {}) => {
  if (!employee?.id) return { expenses: [], shifts: [] };
  const expenseRows = expenses.filter((e) => e.employeeId === employee.id);
  const shifts = [];
  const scan = (record, kind) => {
    (record.workShifts || []).forEach((shift, idx) => {
      const isSupervisor = shift.supervisorEmployeeId === employee.id;
      const isWorker = (shift.workers || []).some((w) => w.employeeId === employee.id);
      if (!isSupervisor && !isWorker) return;
      shifts.push({
        kind,
        ref: record.invoiceNumber || record.quotationNumber || record.id?.slice(0, 8),
        customer: record.customerName,
        role: isSupervisor ? 'Supervisor' : 'Shaqaale',
        shiftPeriod: shift.shiftPeriod,
        startTime: shift.startTime,
        endTime: shift.endTime,
        shiftDate: shift.shiftDate,
        recordId: record.id,
        movingDate: record.movingDate,
        fromLocation: record.fromLocation,
        toLocation: record.toLocation,
        serviceType: record.serviceType,
        vehicleName: shift.vehicleName,
        targoNumber: shift.targoNumber,
        cartonCount: shift.cartonCount
      });
    });
  };
  invoices.forEach((inv) => scan(inv, 'invoice'));
  quotations.forEach((q) => scan(q, 'quotation'));
  return { expenses: expenseRows, shifts };
};

/** Project cost sheet line is included in totals / PDF / kharash post. */
export const isProjectCostLineSelected = (line) => {
  if (!line) return false;
  if (line.included === false) return false;
  if (line.included === true) return true;
  return toMoney(line.amount) > 0;
};

export const getActiveProjectCostLines = (lines = []) =>
  (lines || []).filter(isProjectCostLineSelected);

export const normalizeProjectCostLines = (lines = [], { defaultIncludedFromAmount = true } = {}) =>
  (lines || []).map((line, index) => {
    const amount = toMoney(line.amount);
    let included = line.included;
    if (included === undefined && defaultIncludedFromAmount) {
      included = amount > 0;
    }
    if (included === undefined) included = false;
    return {
      ...line,
      lineNo: line.lineNo ?? index + 1,
      included: Boolean(included)
    };
  });

export const computeProjectCostTotals = (sheet) => {
  const activeLines = getActiveProjectCostLines(sheet?.lines || []);
  const qarashkaTotal = activeLines.reduce((sum, l) => sum + toMoney(l.amount), 0);
  const projectAmount = toMoney(sheet?.projectAmount);
  return {
    activeLines,
    qarashkaTotal,
    netIncome: projectAmount - qarashkaTotal
  };
};

const projectCostLineDateKey = (value) => {
  const d = toJsDate(value);
  if (!d) return '';
  try {
    return format(d, 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

/** Which table columns to render on project cost PDF (only columns with data). */
export const getProjectCostPdfColumns = (sheet, activeLines) => {
  const sheetSupervisor = String(sheet?.supervisor || '').trim();
  const sheetDateKey = projectCostLineDateKey(sheet?.jobDate);

  const showDate = activeLines.some((line) => {
    const key = projectCostLineDateKey(line.date || sheet?.jobDate);
    return key && sheetDateKey && key !== sheetDateKey;
  });

  const showSupervisor = activeLines.some((line) => {
    const sup = String(line.supervisor || sheetSupervisor || '').trim();
    return sup && sup !== sheetSupervisor;
  });

  return { showDate, showSupervisor };
};

export const truncateText = (text, maxLength = 30) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/** Escape a value for CSV (RFC-style quoted fields). */
export const escapeCsvCell = (value) => {
  const s = value === null || value === undefined ? '' : String(value);
  return `"${s.replace(/"/g, '""')}"`;
};

/** Trigger browser download of a CSV file (UTF-8 with BOM for Excel). */
export const downloadCsvFile = (filename, headers, rows) => {
  const lines = [
    headers.map(escapeCsvCell).join(','),
    ...rows.map((row) => row.map(escapeCsvCell).join(','))
  ];
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};